// frontend/src/components/wizard/StepReview/QualityRulesSummary.jsx
import React, { useState } from 'react';
import { useLanguageStore } from '../../../store/useLanguageStore';
import {
  ShieldCheck, ChevronDown, ChevronUp, AlertTriangle, ShieldBan,
} from 'lucide-react';
import { QUALITY_RULES } from '../../../utils/qualityRules';

/**
 * Panel colapsable en StepReview que muestra un resumen read-only
 * de las quality rules configuradas en el mapping.
 *
 * Props:
 *   mappings — array: el fieldMappings completo (con qualityRules por campo)
 */
export function QualityRulesSummary({ mappings }) {
  const { t } = useLanguageStore();
  const [expanded, setExpanded] = useState(false);

  // Extraer campos que tienen quality rules
  const fieldsWithRules = (mappings || [])
    .filter(m => {
      if (!m?.target || m.type === 'ignore' || m.type === 'unmapped') return false;
      return Array.isArray(m.qualityRules) && m.qualityRules.length > 0;
    })
    .map(m => ({
      target: m.target,
      rules: m.qualityRules,
    }));

  if (fieldsWithRules.length === 0) return null;

  const totalRules = fieldsWithRules.reduce((sum, f) => sum + f.rules.length, 0);
  const hasBlocks = fieldsWithRules.some(f => f.rules.some(r => r.onViolation === 'block'));

  return (
    <div className="border border-emerald-200 rounded-2xl overflow-hidden bg-emerald-50/30">
      {/* Header — siempre visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-emerald-50/50 transition"
      >
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-xl">
            <ShieldCheck className="w-5 h-5 text-emerald-600" />
          </div>
          <div className="text-left">
            <h4 className="text-sm font-bold text-slate-700">
              {t('data_rules.summary_title')}
            </h4>
            <p className="text-xs text-slate-500">
              {t('data_rules.configured_for').replace('{count}', fieldsWithRules.length)}
              {' · '}
              {t('data_rules.rules_count').replace('{count}', totalRules)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {hasBlocks && (
            <span className="px-2 py-0.5 text-[10px] font-semibold bg-red-100 text-red-700 rounded-full">
              BLOCK
            </span>
          )}
          {expanded
            ? <ChevronUp className="w-5 h-5 text-slate-400" />
            : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </div>
      </button>

      {/* Body — expandible */}
      {expanded && (
        <div className="px-5 pb-4 space-y-3">
          {fieldsWithRules.map((field, fIdx) => (
            <div key={fIdx} className="bg-white rounded-xl p-3 border border-slate-100">
              <p className="text-sm font-semibold text-slate-700 mb-2 font-mono">
                {field.target}
              </p>
              <div className="space-y-1.5">
                {field.rules.map((rule, rIdx) => (
                  <div
                    key={rIdx}
                    className="flex items-center justify-between text-xs py-1"
                  >
                    <div className="flex items-center gap-2 text-slate-600">
                      <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
                      <span>{t(`data_rules.rule_${rule.ruleType}`)}</span>
                      {/* Mostrar params resumidos */}
                      {rule.ruleType === 'REGEX' && rule.params?.pattern && (
                        <span className="text-slate-400 font-mono text-[10px] max-w-[200px] truncate">
                          {rule.params.pattern}
                        </span>
                      )}
                      {rule.ruleType === 'RANGE' && (
                        <span className="text-slate-400">
                          [{rule.params?.min ?? '∞'}, {rule.params?.max ?? '∞'}]
                        </span>
                      )}
                      {(rule.ruleType === 'MIN_LENGTH' || rule.ruleType === 'MAX_LENGTH') && (
                        <span className="text-slate-400">
                          {rule.params?.length}
                        </span>
                      )}
                      {rule.ruleType === 'ALLOWED_VALUES' && rule.params?.values?.length > 0 && (
                        <span className="text-slate-400">
                          [{rule.params.values.slice(0, 3).join(', ')}
                          {rule.params.values.length > 3 ? ` +${rule.params.values.length - 3}` : ''}]
                        </span>
                      )}
                    </div>

                    {/* Badge warn/block */}
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${rule.onViolation === 'block'
                        ? 'bg-red-50 text-red-600'
                        : 'bg-amber-50 text-amber-600'
                      }`}>
                      {rule.onViolation === 'block'
                        ? <ShieldBan className="w-3 h-3" />
                        : <AlertTriangle className="w-3 h-3" />}
                      {t(`data_rules.mode_${rule.onViolation}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
